/**
 * SuggestedCareManagers: what an intake card says about who could take
 * the case. Asks the assignment-suggestions route and shows the
 * covering-unit line, then the two-block table (SuggestedCareManagersTable:
 * available, then not available, nearest first inside each). Rendered in
 * two places with the same look: inline under the first Ready for
 * Assignment checklist item, where the unit lead decides, and inside the
 * Assign care manager dialog. Selecting a row is the caller's action;
 * nothing here assigns.
 *
 * A card with a client asks about the client: the route answers from
 * their home address (basis 'home_address') and says so in the lines
 * below, or from the card's ZIP when it cannot. A card with only a ZIP
 * asks about the ZIP.
 */

import { type ReactNode } from 'react';
import { SuggestedCareManagersTable, type SuggestedCareManagerRow } from '@/components/capacity/SuggestedCareManagersTable';
import { useTenant } from '@/lib/auth';
import { useAssignmentSuggestions, type AssignmentSuggestionsWire } from '@/lib/capacityApi';
import { endSentence } from '@/lib/sentence';

export interface SuggestedCareManagersProps {
  /** The card's ZIP (work_items.prospect_zip); the fallback when the card
   *  has a client. */
  zip: string;
  /** The card's client, when it has one: the client's home address decides. */
  clientId?: string | null;
  selectedId: string | null;
  onSelect: (row: SuggestedCareManagerRow) => void;
  /** Container, unit-line, list, row and cell testids hang off this. */
  testIdPrefix: string;
}

/** "<unit> covers <county>." and its plural and none forms. */
function coversSentence(data: AssignmentSuggestionsWire, county: string): string {
  if (data.covering_units.length === 0) return endSentence(`No unit covers ${county}`);
  if (data.covering_units.length === 1) return endSentence(`${data.covering_units[0]!.unit_name} covers ${county}`);
  return endSentence(`${data.covering_units.map((u) => u.unit_name).join(' and ')} cover ${county}`);
}

/** The unit line for an answer read from the client's home address. */
function homeAddressLine(data: AssignmentSuggestionsWire): string {
  const notOnMap = "Add the client's home address to see nearby care managers.";
  if (data.point === null) {
    return data.county_name === null ? notOnMap : `${notOnMap} ${coversSentence(data, data.county_name)}`;
  }
  if (data.county_name === null) return "County not on file for this client's home address.";
  return coversSentence(data, data.county_name);
}

export function SuggestedCareManagers({ zip, clientId = null, selectedId, onSelect, testIdPrefix }: SuggestedCareManagersProps): ReactNode {
  const { tenantSlug } = useTenant();
  const query = useAssignmentSuggestions(tenantSlug, { clientId, zip });
  const data = query.data;
  const valid = data !== undefined && Array.isArray(data.covering_units);

  let line: string;
  if (query.isPending) line = clientId !== null ? "Looking up this client's home address…" : `Looking up ${zip}…`;
  else if (query.isError || !valid) line = clientId !== null ? "Could not look up this client's address." : `Could not look up ${zip}.`;
  else if (data.basis === 'home_address') line = homeAddressLine(data);
  else if (data.basis === null) line = 'Add a home address or ZIP to see suggested care managers.';
  else if (data.point === null) line = `${zip} could not be placed.`;
  else if (data.county_name === null) line = `County not on file for ${zip}.`;
  else line = coversSentence(data, data.county_name);

  const rows = valid && data.point !== null ? { available: data.available, unavailable: data.unavailable } : null;
  const count = rows ? rows.available.length + rows.unavailable.length : 0;
  const approximateHome = valid && data.basis === 'home_address' && data.point?.precision === 'zip';

  return (
    <div data-testid={testIdPrefix}>
      <p className="text-sm font-semibold text-sage-800 mb-2" data-testid={`${testIdPrefix}-unit-line`}>{line}</p>
      {approximateHome && (
        <p className="text-xs text-ink-soft mb-2" data-testid={`${testIdPrefix}-approximate`}>
          Approximate: only the town or ZIP of the home address was found.
        </p>
      )}
      {rows && count === 0 && <p className="text-xs text-ink-soft">Add care managers' home addresses on the People page to see distances.</p>}
      {rows && count > 0 && (
        <SuggestedCareManagersTable
          available={rows.available}
          unavailable={rows.unavailable}
          selectedId={selectedId}
          onSelect={(rosterId) => {
            const row = [...rows.available, ...rows.unavailable].find((r) => r.roster_id === rosterId);
            if (row) onSelect(row);
          }}
          listTestId={`${testIdPrefix}-list`}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
}
